// User and User-related Types

export enum UserRole {
  ADMIN = 'admin',
  COORDINATOR = 'coordinator',
  TRAINING_MANAGER = 'training_manager', // Training Manager role (same as Manager)
  SUPERVISOR = 'supervisor',
  INSTRUCTOR = 'instructor',
  ATTENDEE = 'attendee',
  GUEST = 'guest',
  // K9-specific roles
  K9_ADMIN = 'k9_admin',
  K9_SUPERVISOR = 'k9_supervisor',
  K9_HANDLER = 'k9_handler',
  K9_TECH = 'k9_tech',
  /** Paid vendor directory operator */
  VENDOR = 'vendor',
  /** Full blog author (may draft/submit; publish is editorial) */
  BLOG_AUTHOR = 'blog_author',
  /** Pending blog author funnel — draft/submit only until promoted */
  BLOG_AUTHOR_PENDING = 'blog_author_pending',
}

export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  SUSPENDED = 'suspended'
}

export interface UserRoleInfo {
  id: number;
  name: string;
  display_name: string;
}

export interface User {
  id: number;
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  suffix?: string;
  professional_suffix?: string;
  phone?: string;
  date_of_birth?: Date;
  gender?: 'male' | 'female';
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  city_id?: number;
  city_other?: string;
  county_id?: number;
  state_id?: number;
  state_other?: string;
  state_name?: string;
  zipcode_id?: number;
  zipcode_value?: string;
  zipcode_other?: string;
  dob?: Date;
  agency_id?: number;
  agency_name?: string;
  office_id?: number;
  office_name?: string;
  office_address?: string;
  office_phone?: string;
  badge_number?: string;
  rank?: string;
  department?: string;
  years_of_service?: number;
  first_commission_date?: Date;
  license_date?: Date;
  military_served?: boolean;
  /** Agency type name (from agency_types) for profile completeness when user has agency_id */
  agency_type_name?: string;
  certifications?: string[];
  specializations?: string[];
  military_branch_id?: number;
  military_service_id?: number;
  rank_id?: number;
  state_commission_id?: string;
  commission_id?: string;
  current_group_id?: number;
  /** @deprecated Use roles array instead - this field is maintained for backward compatibility only */
  role?: UserRole | string;
  /** Primary field for user roles - fetched from user_roles table */
  roles?: UserRoleInfo[] | string[];
  status: UserStatus;
  is_email_verified: boolean;
  is_phone_verified?: boolean;
  is_manually_registered: boolean;
  agree_to_term_condition: boolean;
  reset_password_status: boolean;
  temp_password_update_status: boolean;
  two_factor_secret?: string;
  two_factor_enabled: boolean;
  passkey_enabled?: boolean;
  dev_access_enabled?: boolean;
  /** Google Play / App Store read-only reviewer account */
  is_store_reviewer?: boolean;
  mfaVerified?: boolean; // MFA verification status for current session
  email_notifications?: boolean;
  event_updates?: boolean;
  marketing_communications?: boolean;
  system_alerts?: boolean;
  training_reminders?: boolean;
  certificate_expiry?: boolean;
  email_verification_token?: string;
  reset_password_token?: string;
  google_captcha_reference?: string;
  profile_image?: string;
  agency_id_card_image?: string;
  goverment_id_card_image?: string;
  ip_address?: string;
  user_agent?: string;
  last_logged_in?: Date;
  notes?: string;
  phone_notes?: string;
  created_by?: number;
  updated_by?: number;
  created_at: Date;
  updated_at: Date;
  is_premium_user?: boolean;
  premium_expires_at?: Date;
  premium_type?: string;
  premium_coupon_code?: string;
  subscription_tier?: string;
  subscription_status?: string;
  subscription_type?: string;
  subscription_start_date?: Date;
  subscription_end_date?: Date;
  stripe_subscription_id?: string;
  is_tacticore_user?: boolean;
  tacticore_trial_used?: boolean;
  tacticore_subscription_status?: string;
  tacticore_trial_ends_at?: Date;
  tacticore_subscription_ends_at?: Date;
  tacticore_role?: string;
  tacticore_roles?: string[];
  tacticore_joined_at?: Date;
  // Additional user properties
  currency?: string;
  measurement_system?: string;
  notification_preferences?: Record<string, boolean>;
  privacy_settings?: Record<string, boolean>;
  last_login_at?: Date;
  last_activity_at?: Date;
  login_count?: number;
  failed_login_count?: number;
  locked_until?: Date;
  password_changed_at?: Date;
  password_reset_token?: string;
  password_reset_expires?: Date;
  remember_token?: string;
  deleted_at?: Date;
}

export interface UserPreferences {
  email_notifications?: boolean;
  event_updates?: boolean;
  marketing_communications?: boolean;
  system_alerts?: boolean;
  training_reminders?: boolean;
  certificate_expiry?: boolean;
  theme?: 'light' | 'dark' | 'auto';
  language?: string;
  timezone?: string;
  email_frequency?: 'immediate' | 'daily' | 'weekly';
}

export interface UserSettings {
  profile_visibility?: 'public' | 'private' | 'friends_only';
  search_visibility?: boolean;
  activity_feed_visibility?: boolean;
  training_progress_visibility?: boolean;
  achievement_visibility?: boolean;
  contact_info_visibility?: 'public' | 'private' | 'friends_only';
  social_features_enabled?: boolean;
  auto_save_drafts?: boolean;
  default_page_size?: number;
}

export interface UserProfile {
  // Additional profile-specific fields
  profile_completion_percentage?: number;
  last_activity?: Date;
  preferences?: {
    email_notifications?: boolean;
    event_updates?: boolean;
    marketing_communications?: boolean;
    system_alerts?: boolean;
    training_reminders?: boolean;
    certificate_expiry?: boolean;
  };
}

export interface UserSearchParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  role?: UserRole;
  agency_id?: number;
  active?: boolean;
}

export interface UserFormData {
  first_name: string;
  middle_name?: string;
  last_name: string;
  suffix?: string;
  professional_suffix?: string;
  email: string;
  phone?: string;
  dob?: string;
  gender?: 'male' | 'female';
  address?: string;
  city_id?: number;
  city_other?: string;
  county_id?: number;
  state_id?: number;
  zipcode_id?: number;
  zipcode_other?: string;
  role: 'admin' | 'coordinator' | 'manager' | 'supervisor' | 'instructor' | 'attendee' | 'guest' | 'employer' | 'k9_admin' | 'k9_supervisor' | 'k9_handler' | 'k9_tech';
  roles?: string[]; // Multiple roles for future expansion
  status: 'active' | 'inactive' | 'pending' | 'suspended';
  agency_id?: number;
  badge_number?: string;
  department?: string;
  years_of_service?: number;
  military_branch_id?: number;
  military_service_id?: number;
  rank_id?: number;
  state_commission_id?: string;
  commission_id?: string;
  current_group_id?: number;
  certifications?: string[];
  specializations?: string[];
  is_email_verified?: boolean;
  is_manually_registered?: boolean;
  agree_to_term_condition?: boolean;
  reset_password_status?: boolean;
  temp_password_update_status?: boolean;
  two_factor_enabled?: boolean;
  dev_access_enabled?: boolean;
  password?: string;
  confirm_password?: string;
  // Additional fields from database schema
  notes?: string;
  phone_notes?: string;
  profile_image?: string;
  goverment_id_card_image?: string;
  agency_id_card_image?: string;
  ip_address?: string;
  user_agent?: string;
  google_captcha_reference?: string;
  last_logged_in?: string;
  created_by?: number;
  updated_by?: number;
  two_factor_secret?: string;
  email_notifications?: boolean;
  event_updates?: boolean;
  marketing_communications?: boolean;
  system_alerts?: boolean;
  training_reminders?: boolean;
  certificate_expiry?: boolean;
  is_tacticore_user?: boolean;
  tacticore_trial_used?: boolean;
  tacticore_subscription_status?: 'none' | 'trial' | 'active' | 'expired' | 'cancelled';
  tacticore_trial_ends_at?: string;
  tacticore_subscription_ends_at?: string;
  tacticore_role?: 'owner' | 'admin' | 'instructor' | 'student' | 'viewer'; // legacy single role
  /** New multi-role: admin, supervisor, curriculum_developer, instructor */
  tacticore_roles?: string[];
  tacticore_joined_at?: string;
  // Emergency contact and medical information
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  medical_conditions?: string;
  allergies?: string;
  medications?: string;
  // Additional user preferences
  currency?: string;
  measurement_system?: string;
  notification_preferences?: Record<string, boolean>;
  privacy_settings?: Record<string, boolean>;
  last_login_at?: string;
  last_activity_at?: string;
  login_count?: number;
  failed_login_count?: number;
  locked_until?: string;
  password_changed_at?: string;
  password_reset_token?: string;
  password_reset_expires?: string;
  remember_token?: string;
  deleted_at?: string;
  // Premium Subscription Fields
  subscription_status?: 'active' | 'expired' | 'none';
  subscription_tier?: string;
  subscription_start_date?: string;
  subscription_end_date?: string;
  subscription_type?: string;
  // Premium fields (legacy)
  is_premium_user?: boolean;
  premium_expires_at?: string;
  premium_type?: string;
  premium_coupon_code?: string;
  premium_last_modified_by?: number;
  premium_last_modified_at?: string;
  // Premium Admin Override Fields
  premium_override_enabled?: boolean;
  premium_override_type?: 'customer_satisfaction' | 'promotion' | 'manual_adjustment' | 'other';
  premium_override_action?: 'grant' | 'extend' | 'revoke' | 'modify';
  premium_override_new_end_date?: string;
  premium_override_reason?: string;
  premium_override_notes?: string;
}
